// Password policy — single source of truth for the MyMoolah wallet (frontend).
//
// Policy (NIST SP 800-63B aligned: favour length, do not force composition):
//   REQUIRED : at least PASSWORD_MIN_LENGTH characters, at least one letter,
//              at least one number.
//   OPTIONAL : uppercase and special characters are encouraged (they make a
//              password stronger) but are NOT required and never block a user.
//   ACCEPTED : any character may be used, including any special character.
//
// Hardening (2026-08-14, after the credential-guessing incident):
//   - CONTEXTUAL : a password must NOT contain the user's own first name,
//                  surname, phone number, ID number, or email local-part.
//   - BLOCKLIST  : a small set of the most common weak passwords/patterns.
//   - BREACH     : a HaveIBeenPwned k-anonymity breach check runs on the
//                  BACKEND only (authoritative); it is not mirrored here.
//
// MUST stay in sync with the backend mirror at `utils/passwordPolicy.js`.
// The backend is the authoritative enforcement — this module only gives the
// user immediate feedback.

export const PASSWORD_MIN_LENGTH = 8;
// Capped within bcrypt's 72-byte effective limit; NIST recommends allowing >= 64.
export const PASSWORD_MAX_LENGTH = 64;

export const PASSWORD_REQUIREMENTS_MESSAGE =
  'Password must be at least 8 characters and include at least one letter and one number';

// Generic message — intentionally does NOT reveal which profile value matched.
export const PASSWORD_CONTEXTUAL_MESSAGE =
  'Password must not contain your name, surname, mobile number, ID number, or email address.';

export const PASSWORD_BREACHED_MESSAGE =
  'This password is too common or has appeared in a data breach. Please choose a different password.';

export const PASSWORD_STRENGTH_MESSAGE =
  `${PASSWORD_REQUIREMENTS_MESSAGE}, and must not contain your personal details.`;

/** Small blocklist of the most common weak passwords (normalized form). */
const BLOCKED_PASSWORDS: ReadonlySet<string> = new Set([
  'password', 'password1', 'password12', 'password123', 'passw0rd', 'p455w0rd',
  'qwerty', 'qwerty1', 'qwerty12', 'qwerty123', 'letmein', 'letmein1',
  'welcome', 'welcome1', 'welcome123', 'admin', 'admin1', 'admin123',
  'mymoolah', 'mymoolah1', 'mymoolah123', 'moolah', 'moolah123',
  'iloveyou', 'monkey', 'monkey1', 'dragon', 'dragon1', 'sunshine', 'princess',
  'football', 'baseball', 'superman', 'batman', 'trustno1', 'master', 'hello123',
  '12345678', '123456789', '1234567890', '0987654321', '11111111', '00000000',
  '123123123', 'abc12345', 'abcd1234', 'pass1234', 'pass12345',
]);

/** Lowercase and strip everything that is not a letter or digit. */
export function normalizeForMatch(value: unknown): string {
  return String(value == null ? '' : value).toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Profile fields used for the contextual check. */
export interface PasswordContext {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  idNumber?: string;
  email?: string;
}

/** Build the set of profile-derived tokens that must not appear in a password. */
function buildDerivedTokens(context: PasswordContext): string[] {
  const ctx = context || {};
  const tokens: string[] = [];
  const push = (v: unknown, minLen = 3) => {
    const n = normalizeForMatch(v);
    if (n && n.length >= minLen) tokens.push(n);
  };

  push(ctx.firstName);
  push(ctx.lastName);
  if (ctx.email) push(String(ctx.email).split('@')[0]);

  const phoneDigits = String(ctx.phoneNumber || '').replace(/\D/g, '');
  if (phoneDigits.length >= 6) {
    tokens.push(phoneDigits);
    tokens.push(phoneDigits.slice(-9));
    tokens.push(phoneDigits.slice(-6));
  }

  const idDigits = String(ctx.idNumber || '').replace(/\D/g, '');
  if (idDigits.length >= 6) tokens.push(idDigits);

  return tokens;
}

/** Does the (normalized) password contain any of the user's own profile tokens? */
export function evaluateContextual(password: string, context: PasswordContext): { ok: boolean; hits: string[] } {
  const pw = normalizeForMatch(password);
  const hits: string[] = [];
  for (const token of buildDerivedTokens(context)) {
    if (token && pw.includes(token)) hits.push(token);
  }
  return { ok: hits.length === 0, hits };
}

/** True if the password is on the common-weak blocklist. */
export function isBlockedPassword(password: string): boolean {
  return BLOCKED_PASSWORDS.has(normalizeForMatch(password));
}

export interface PasswordChecks {
  // Required
  minLength: boolean;
  maxLength: boolean;
  hasLetter: boolean;
  hasNumber: boolean;
  // Optional strength signals (informational only)
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasSpecialChar: boolean;
}

export interface PasswordEvaluation {
  isValid: boolean;
  checks: PasswordChecks;
}

export function evaluatePassword(password: string): PasswordEvaluation {
  const pw = typeof password === 'string' ? password : '';
  const checks: PasswordChecks = {
    minLength: pw.length >= PASSWORD_MIN_LENGTH,
    maxLength: pw.length <= PASSWORD_MAX_LENGTH,
    hasLetter: /[A-Za-z]/.test(pw),
    hasNumber: /\d/.test(pw),
    hasUppercase: /[A-Z]/.test(pw),
    hasLowercase: /[a-z]/.test(pw),
    hasSpecialChar: /[^A-Za-z0-9]/.test(pw),
  };
  const isValid =
    checks.minLength && checks.maxLength && checks.hasLetter && checks.hasNumber;
  return { isValid, checks };
}

export function isValidPassword(password: string): boolean {
  return evaluatePassword(password).isValid;
}

/**
 * Synchronous strength evaluation: base policy + contextual + blocklist.
 * The async breach check is enforced on the backend and is not mirrored here.
 */
export function evaluateStrength(password: string, context?: PasswordContext): { ok: boolean; errors: string[] } {
  const errors: string[] = [];
  if (!isValidPassword(password)) {
    errors.push(PASSWORD_REQUIREMENTS_MESSAGE);
  }
  if (isBlockedPassword(password)) {
    errors.push(PASSWORD_BREACHED_MESSAGE);
  }
  if (context && !evaluateContextual(password, context).ok) {
    errors.push(PASSWORD_CONTEXTUAL_MESSAGE);
  }
  return { ok: errors.length === 0, errors };
}

/**
 * Human-readable "how to fix" list for the failed REQUIRED rules only.
 * Used by the registration/reset explainer modal.
 */
export function getPasswordErrors(password: string): string[] {
  const { checks } = evaluatePassword(password);
  const errors: string[] = [];
  if (!checks.minLength) {
    errors.push(`Use at least ${PASSWORD_MIN_LENGTH} characters`);
  }
  if (!checks.hasLetter) {
    errors.push('Include at least one letter (a-z or A-Z)');
  }
  if (!checks.hasNumber) {
    errors.push('Include at least one number (0-9)');
  }
  if (!checks.maxLength) {
    errors.push(`Use no more than ${PASSWORD_MAX_LENGTH} characters`);
  }
  return errors;
}
